import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Plus, Edit2, Trash2, ExternalLink, IndianRupee, FolderPlus, Folder, X } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';

const WishlistPage = () => {
  const navigate = useNavigate();
  const [wishlistItems, setWishlistItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    link: '',
    image_url: '',
    description: '',
    category_id: null
  });
  const [newCategoryName, setNewCategoryName] = useState('');

  const API_URL = window.location.origin;

  useEffect(() => {
    fetchCategories();
    fetchWishlist();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wishlist/categories`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchWishlist = async () => {
    try {
      const response = await fetch(`${API_URL}/api/wishlist`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setWishlistItems(data.items || []);
      }
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ name: newCategoryName })
      });

      if (response.ok) {
        await fetchCategories();
        setNewCategoryName('');
        setShowCategoryModal(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create category');
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Failed to create category');
    }
  };

  const handleUpdateCategory = async (categoryId, newName) => {
    if (!newName.trim()) {
      alert('Please enter a category name');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/categories/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ name: newName })
      });

      if (response.ok) {
        await fetchCategories();
        setEditingCategory(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      alert('Failed to update category');
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!window.confirm('Delete this category? Items will be moved to uncategorized.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/categories/${categoryId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchCategories();
        await fetchWishlist();
        if (selectedCategory === categoryId) {
          setSelectedCategory('all');
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter an item name');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id || null,
        price: formData.price ? parseFloat(formData.price) : null,
        link: formData.link || null,
        image_url: formData.image_url || null,
        description: formData.description || null
      };

      const response = await fetch(`${API_URL}/api/wishlist/items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchWishlist();
        setShowAddModal(false);
        resetForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add item');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Failed to add item');
    }
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Please enter an item name');
      return;
    }

    try {
      const payload = {
        name: formData.name,
        category_id: formData.category_id || null,
        price: formData.price ? parseFloat(formData.price) : null,
        link: formData.link || null,
        image_url: formData.image_url || null,
        description: formData.description || null
      };

      const response = await fetch(`${API_URL}/api/wishlist/items/${editingItem.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await fetchWishlist();
        setEditingItem(null);
        resetForm();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update item');
      }
    } catch (error) {
      console.error('Error updating item:', error);
      alert('Failed to update item');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/wishlist/items/${itemId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        await fetchWishlist();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Failed to delete item');
    }
  };

  const openAddModal = () => {
    resetForm();
    setShowAddModal(true);
  };

  const openEditModal = (item) => {
    setFormData({
      name: item.name,
      category_id: item.category_id || null,
      price: item.price || '',
      link: item.link || '',
      image_url: item.image_url || '',
      description: item.description || ''
    });
    setEditingItem(item);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      link: '',
      image_url: '',
      description: '',
      category_id: null
    });
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingItem(null);
    resetForm();
  };

  const filteredItems = selectedCategory === 'all' 
    ? wishlistItems
    : selectedCategory === 'uncategorized'
    ? wishlistItems.filter(item => !item.category_id)
    : wishlistItems.filter(item => item.category_id === selectedCategory);

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-contribo-navy via-contribo-navy to-gray-900 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-contribo-navy to-contribo-gold p-4 shadow-xl">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingBag className="text-white" size={24} />
              <h1 className="text-xl sm:text-3xl font-bold text-white">My Wishlist</h1>
            </div>
            <button
              onClick={openAddModal}
              className="bg-white text-contribo-navy px-3 py-2 rounded-lg font-semibold hover:bg-gray-100 transition flex items-center gap-1 text-sm"
            >
              <Plus size={18} />
              <span className="hidden xs:inline">Add</span>
            </button>
          </div>
          
          <button
            onClick={() => setShowCategoryModal(true)}
            className="bg-purple-600 text-white px-3 py-2 rounded-lg font-semibold hover:bg-purple-700 transition flex items-center justify-center gap-2 text-sm w-full"
          >
            <FolderPlus size={18} />
            Manage Categories
          </button>
        </div>

        {/* Category Filter */}
        <div className="mt-4 flex gap-2 flex-wrap">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-3 py-1.5 rounded-lg font-semibold transition text-sm ${
              selectedCategory === 'all'
                ? 'bg-white text-contribo-navy'
                : 'bg-contribo-navy text-white border border-white'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setSelectedCategory('uncategorized')}
            className={`px-3 py-1.5 rounded-lg font-semibold transition text-sm ${
              selectedCategory === 'uncategorized'
                ? 'bg-white text-contribo-navy'
                : 'bg-contribo-navy text-white border border-white'
            }`}
          >
            Uncategorized
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-3 py-1.5 rounded-lg font-semibold transition flex items-center gap-1 text-sm ${
                selectedCategory === category.id
                  ? 'bg-white text-contribo-navy'
                  : 'bg-contribo-navy text-white border border-white'
              }`}
            >
              <Folder size={14} />
              <span className="truncate max-w-[100px]">{category.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-3 py-4 max-w-full">
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 px-4">
            <ShoppingBag className="mx-auto text-gray-400 mb-4" size={48} />
            <h2 className="text-lg sm:text-2xl font-bold text-white mb-2">
              {selectedCategory === 'all' ? 'Your wishlist is empty' : 'No items in this category'}
            </h2>
            <p className="text-sm text-gray-300 mb-6">Add items from your favorite shopping sites</p>
            <button
              onClick={openAddModal}
              className="bg-contribo-gold text-contribo-navy px-5 py-2.5 rounded-lg font-bold hover:bg-yellow-500 transition text-sm"
            >
              Add Your First Item
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition w-full"
              >
                {item.image_url ? (
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="w-full h-40 object-cover"
                  />
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <ShoppingBag className="text-gray-400" size={48} />
                  </div>
                )}

                <div className="p-3">
                  {item.category_id && (
                    <div className="flex items-center gap-1 mb-1.5">
                      <Folder className="text-purple-600" size={12} />
                      <span className="text-xs text-purple-600 font-semibold truncate">
                        {getCategoryName(item.category_id)}
                      </span>
                    </div>
                  )}
                  
                  <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 line-clamp-2">{item.name}</h3>
                  
                  {item.price && (
                    <div className="flex items-center gap-1.5 mb-2">
                      <IndianRupee className="text-contribo-gold" size={16} />
                      <span className="text-base font-semibold text-gray-700">
                        ₹{item.price.toLocaleString()}
                      </span>
                    </div>
                  )}

                  {item.description && (
                    <p className="text-gray-600 text-xs mb-3 line-clamp-2">{item.description}</p>
                  )}

                  <div className="flex flex-col gap-2 mt-3">
                    {item.link && (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full bg-contribo-navy text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-opacity-90 transition flex items-center justify-center gap-1.5"
                      >
                        <ExternalLink size={14} />
                        View Product
                      </a>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => openEditModal(item)}
                        className="flex-1 bg-blue-500 text-white px-2 py-2 rounded-lg text-xs font-semibold hover:bg-blue-600 transition flex items-center justify-center gap-1"
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteItem(item.id)}
                        className="flex-1 bg-red-500 text-white px-2 py-2 rounded-lg text-xs font-semibold hover:bg-red-600 transition flex items-center justify-center gap-1"
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Item Modal */}
      {(showAddModal || editingItem) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <h2 className="text-xl font-bold text-gray-800 mb-3">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>

              <form onSubmit={editingItem ? handleUpdateItem : handleAddItem}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={formData.category_id || ''}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent"
                    >
                      <option value="">Uncategorized</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Item Name *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent"
                      placeholder="e.g., iPhone 15 Pro"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Price (₹)
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent"
                      placeholder="e.g., 129900"
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Product Link (URL)
                    </label>
                    <input
                      type="url"
                      value={formData.link}
                      onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent break-all"
                      placeholder="https://amazon.in/..."
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.image_url}
                      onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent break-all"
                      placeholder="https://example.com/image.jpg"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-contribo-gold focus:border-transparent"
                      placeholder="Add any notes about this item..."
                      rows="2"
                    />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 bg-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-gray-400 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-contribo-gold text-contribo-navy px-3 py-2 rounded-lg text-sm font-bold hover:bg-yellow-500 transition"
                  >
                    {editingItem ? 'Update' : 'Add'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Category Management Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex justify-between items-center mb-3">
                <h2 className="text-xl font-bold text-gray-800">Manage Categories</h2>
                <button
                  onClick={() => setShowCategoryModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Add New Category */}
              <div className="mb-4 p-3 bg-purple-50 rounded-lg border-2 border-purple-200">
                <h3 className="font-semibold text-sm mb-2 text-gray-800">Add New Category</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newCategoryName}
                    onChange={(e) => setNewCategoryName(e.target.value)}
                    placeholder="Category name..."
                    className="flex-1 px-2.5 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddCategory}
                    className="bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700 transition whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Existing Categories */}
              <div>
                <h3 className="font-semibold text-sm mb-2 text-gray-800">Your Categories</h3>
                {categories.length === 0 ? (
                  <p className="text-gray-500 text-center py-3 text-xs">No categories yet</p>
                ) : (
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        className="flex items-center gap-2 p-2.5 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        {editingCategory === category.id ? (
                          <>
                            <input
                              type="text"
                              defaultValue={category.name}
                              onBlur={(e) => {
                                if (e.target.value !== category.name) {
                                  handleUpdateCategory(category.id, e.target.value);
                                } else {
                                  setEditingCategory(null);
                                }
                              }}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleUpdateCategory(category.id, e.target.value);
                                }
                              }}
                              autoFocus
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <button
                              onClick={() => setEditingCategory(null)}
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <>
                            <Folder className="text-purple-600 flex-shrink-0" size={16} />
                            <span className="flex-1 font-medium text-gray-800 text-sm truncate">{category.name}</span>
                            <button
                              onClick={() => setEditingCategory(category.id)}
                              className="text-blue-600 hover:text-blue-800 p-1 flex-shrink-0"
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(category.id)}
                              className="text-red-600 hover:text-red-800 p-1 flex-shrink-0"
                            >
                              <Trash2 size={14} />
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-full mt-4 bg-gray-300 text-gray-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-gray-400 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNavigation />
    </div>
  );
};

export default WishlistPage;
